import React, { useEffect, useState } from "react";
import { Dialog, Button, TextField, Box } from "@mui/material";
import { Typography, CircularProgress, Link } from "@mui/joy";
import { SearchRounded } from "@mui-symbols-material/w300";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QuickActionAssetLookup } from "../../graphql/queries/quickActionAssetLookup";
import { ManualEventType } from "../../codegen/graphql";
import {
    QuickActionEventType,
    QuickActionTarget,
    resolveQuickActionTarget,
} from "../../utils/quickActionAssetResolution";
import CreateInstalledBaseEventModal from "../CreateInstalledBaseEventModal";

// The dashboard tiles that a completed install/remove changes. The wizard's own
// defaults name asset-detail queries, none of which are mounted here.
const DASHBOARD_REFETCH = ["AssetsTotal", "AssetTotalByConnectionStatus"];

type ReadyTarget = Extract<QuickActionTarget, { kind: 'ready' }>;

interface QuickActionAssetModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    eventType: QuickActionEventType;
}

/**
 * Dashboard quick action: look an asset up by serial number and, when the backend
 * event matrix allows it, open the install/remove wizard right here — the dashboard
 * stays behind the modal. The wizard shows the asset's nameplate at the top, so the
 * user sees what the serial resolved to without navigating anywhere.
 */
const QuickActionAssetModal: React.FC<QuickActionAssetModalProps> = ({ open, onClose, title, eventType }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [serialNumber, setSerialNumber] = useState("");
    const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
    const [target, setTarget] = useState<ReadyTarget | null>(null);

    // no-cache, not network-only: `AssetInstallationsWidget` reads `searchAssets` with
    // the identical `{ serialNumber: { equals } }` args but a different selection, and
    // `searchAssets` is an id-less wrapper stored inline under ROOT_QUERY — writing our
    // narrower selection there would strip the widget's fields.
    const [lookupAsset, { loading }] = useLazyQuery(QuickActionAssetLookup, {
        fetchPolicy: "no-cache",
    });

    // Reopening the tile always starts from an empty input.
    useEffect(() => {
        if (open) {
            setSerialNumber("");
            setErrorMessage(null);
            setTarget(null);
        }
    }, [open]);

    const blockedMessage = (reason: 'notInstalled' | 'alreadyInstalled' | 'notAllowed'): string => {
        const isExchange = eventType === ManualEventType.ASSET_EXCHANGE;
        if (reason === 'notInstalled') {
            return isExchange
                ? t("This asset is not installed and cannot be exchanged.")
                : t("This asset is not installed and cannot be removed.");
        }
        if (reason === 'alreadyInstalled') return t("This asset is already installed.");
        switch (eventType) {
            case ManualEventType.ASSET_INSTALL: return t("This asset cannot be installed.");
            case ManualEventType.ASSET_EXCHANGE: return t("This asset cannot be exchanged.");
            default: return t("This asset cannot be removed.");
        }
    };

    const ambiguousMessage = (serial: string): React.ReactNode => (
        <>
            {t("Several assets share this serial number.")}{' '}
            <Link
                component="button"
                type="button"
                onClick={() => {
                    onClose();
                    navigate(`/assets?query=${encodeURIComponent(serial)}&fields=SERIAL_NUMBER`);
                }}
            >
                {t("Open asset search")}
            </Link>
        </>
    );

    const handleConfirm = async () => {
        const serial = serialNumber.trim();
        if (!serial) return;
        setErrorMessage(null);

        try {
            const { data } = await lookupAsset({ variables: { serialNumber: serial } });
            const resolved = resolveQuickActionTarget(
                data?.searchAssets?.searchAssetResults,
                data?.searchAssets?.total ?? 0,
                eventType,
            );

            switch (resolved.kind) {
                case 'ready':
                    setTarget(resolved);
                    return;
                case 'notFound':
                    setErrorMessage(t("No asset found with this serial number."));
                    return;
                case 'ambiguous':
                    setErrorMessage(ambiguousMessage(serial));
                    return;
                case 'blocked':
                    setErrorMessage(blockedMessage(resolved.reason));
                    return;
            }
        } catch {
            setErrorMessage(t("Error looking up asset. Please try again."));
        }
    };

    if (target) {
        return (
            <CreateInstalledBaseEventModal
                open
                assetId={target.assetId}
                assetLocationId={target.assetLocationId}
                installedBaseNodeId={target.installedBaseNodeId}
                initialEventType={eventType}
                refetchQueries={DASHBOARD_REFETCH}
                onClose={onClose}
            />
        );
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <BasicModalContent
                onClose={onClose}
                startIcon={<SearchRounded />}
                title={<Typography level="titleSmallLight">{title}</Typography>}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400, p: 2 }}>
                    <TextField
                        label={t("Serial number")}
                        variant="outlined"
                        fullWidth
                        value={serialNumber}
                        onChange={(e) => { setSerialNumber(e.target.value); setErrorMessage(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && serialNumber.trim()) handleConfirm(); }}
                        error={!!errorMessage}
                        helperText={errorMessage}
                        autoFocus
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={onClose}>{t("Cancel")}</Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={loading || !serialNumber.trim()}
                        >
                            {loading ? <CircularProgress size="sm" /> : t("Confirm")}
                        </Button>
                    </Box>
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default QuickActionAssetModal;
